import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { AutoSub, AutoSubOnDestroy, EventDay, EventFilter, FilterStatus, MoteEventService, ProjectService, ScheduleService } from '../../providers';
import { Mote, MoteEventType, MoteType, MoteTypeIconMap } from '../../models';

@AutoSubOnDestroy()
@Component({
    selector: 'schedule-filter',
    templateUrl: './schedule-filter.component.html',
    styleUrls: ['./schedule-filter.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ScheduleFilterComponent implements OnInit {
    // Number of hours included in "soon" / 'now' filter
    private static NOW_EVENT_HOURS = 1;

    /**
     * List of event motes to generate filter options from
     */
    @Input('events') set setEventMotes(ev: { events: Mote[], schedule: Mote }) {
        this.schedule = ev.schedule;
        let eventList = ev.events.filter(mote => mote.parent == this.schedule?.key);

        // Get list of days with events
        let dayList = this.scheduleService.generateEventDayList(eventList);
        // Create a filter for each available day
        this.filterList = this.getDateFilters(dayList);
        // Add now and soon filters to list
        this.addSpecialDateFilters(this.filterList, eventList);

        this.scheduleService.setFilters(this.filterList);
    }

    /**
     * Emits when user presses schedule icon on filter bar
     */
    @Output() openSchedule = new EventEmitter<Mote>();

    /**
     * Emits when a filter is enabled
     */
    @Output() filterToggled = new EventEmitter<EventFilter>();

    /**
     * Schedule to be emitted when pressing the schedule icon
     */
    public schedule: Mote;

    public filterList: EventFilter[] = [];
    public filterStatus: FilterStatus = new FilterStatus('', false);

    // Expose to view
    public MoteTypeIconMap = MoteTypeIconMap;
    public MoteType = MoteType;
    public EVENT_ICON = MoteTypeIconMap[MoteType.EVENT];

    constructor(private projectService: ProjectService, private scheduleService: ScheduleService, private moteEventService: MoteEventService) {
    }

    ngOnInit(): void {
        AutoSub(this).reg['statusSub'] = this.scheduleService.watchStatusMsg().subscribe(status => {
            this.filterStatus = status;
        });
    }

    private addSpecialDateFilters(filterList: EventFilter[], events: Mote[]): EventFilter[] {
        let now = new Date();
        // Create soon start time x hours from now
        let soonStart = new Date(now)
        //soonStart.setMinutes(now.getMinutes() + 1);

        // Create soon end time x hours from now
        let soonEnd = new Date(now)
        soonEnd.setHours(now.getHours() + ScheduleFilterComponent.NOW_EVENT_HOURS);

        // Init soon filter (obs: merge 'now' filter and label soon as 'now')
        let soonFilter = { key: 'soon', label: 'Now', fromDate: soonStart, toDate: soonEnd, events: [], showStarted: true, isEnabled: this.scheduleService.isFilterEnabled('soon') };
        soonFilter.events = this.getEventsInPeriod(soonFilter, events, true);

        // Init now filter
        // let nowFilter = { key: 'now', label: 'Now', fromDate: now, toDate: now, events:[], showStarted: true, isEnabled:this.scheduleService.isFilterEnabled('now') };
        // nowFilter.events = this.getEventsInPeriod(nowFilter, events);
        // filterList.unshift(nowFilter);

        // Add special filters to filter list
        filterList.unshift(soonFilter);

        return filterList;
    }

    private getEventsInPeriod(filter: EventFilter, events: Mote[], showRangeIsWithinEvent: boolean = true): Mote[] {
        // Filter out motes within range of filter
        return events.filter(event => this.scheduleService.isEventWithinFilterRange(event, filter, true, showRangeIsWithinEvent));
    }

    private getDateFilters(dayList: EventDay[]): EventFilter[] {
        return dayList.map((day) => {
            // Generate start time of filter at start of day
            let fromDate = new Date(day.date.getTime());
            fromDate.setHours(0);
            fromDate.setMinutes(0);
            fromDate.setSeconds(0);

            // Generate end time of filter at end of day
            let toDate = new Date(day.date.getTime());
            toDate.setHours(23);
            toDate.setMinutes(59);
            toDate.setSeconds(59);

            // Create filter entry
            let filter: EventFilter = {
                key: day.key,
                label: '',
                fromDate: fromDate,
                toDate: toDate,
                events: day.events,
                showStarted: true,
                isEnabled: this.scheduleService.isFilterEnabled(day.key)
            }
            return filter;
        });
    }

    public toggleFilter(filter: EventFilter) {
        // If all filters are enabled, disable all of them and select only the one pressed
        if(this.isAllFiltersEnabled()) this.disableAllFilters();

        // Toggle filter locally
        filter.isEnabled = !filter.isEnabled;
        // Register new filter state in service
        this.scheduleService.updateFilterState(filter);

        // Get count of all enabled events
        let eventCount = this.enabledEventsCount();
        // Generate status message with event count
        let status = (eventCount > 0) ? $localize`${eventCount} ${eventCount > 1 ? 'events' : 'event'} shown on map` : $localize`No events to show on map`;

        // Show the status
        this.scheduleService.showStatusMsg(status);

        // Notify world (main-overlay) to update motes on map and in swiper 
        this.moteEventService.emitMoteEvent({ type: MoteEventType.MOTE_FILTER_UPDATED });
        // Notify parent that a new filter is enabled (and which motes to zoom to)
        this.filterToggled.emit(filter);
    }

    public enabledEventsCount(): number {
        // Make unique list of filters (because of now they might be in the array twice)
        let eventArrayList = this.filterList.map(filter => filter.isEnabled ? filter.events.map(event => event.key) : []);
        // Flatten event list
        let eventList = [].concat(...eventArrayList);
        // Find only unique items in list
        let uniqueEventList = [...new Set(eventList)];
        // Count total filters enabled in unique filter list
        return uniqueEventList.length;
    }

    public emitOpenSchedule() {
        this.openSchedule.emit(this.schedule);
    }

    public disableAllFilters(): void {
        for(let filter of this.filterList) {
            // Mark off in component
            filter.isEnabled = false;
            // Register state in schedule service
            this.scheduleService.updateFilterState(filter);
        }
    }

    public isAllFiltersEnabled(): boolean {
        let disabledFilters = this.filterList.filter(filter => !filter.isEnabled);
        return (disabledFilters.length == 0);
    }
}
